import { useRef, useState } from 'react';
import { isValidLocator } from '../lib/locator.js';
import { ALL_BANDS, DEFAULT_VISIBLE_BANDS, bandColor } from '../lib/bands.js';
import Filters from './Filters.jsx';

const ROW_LIMITS = [50, 100, 150, 200, 250, 500];
const THEMES = [
  { id: 'dark',  label: 'dark'  },
  { id: 'light', label: 'light' },
];

// SettingsPanel — drawer body. Same content the old sidebar held, plus a
// section ordering tuned for "things you change often" → "things you change once".
export default function SettingsPanel({ settings, onUpdate, onReset, ioPort }) {
  const [locDraft, setLocDraft] = useState(settings.locator);
  const locValid = isValidLocator(locDraft);
  const fileInput = useRef(null);
  const [importMsg, setImportMsg] = useState(null);

  const onExport = () => {
    const blob = new Blob([ioPort.exportJSON()], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'geuspider-settings.json';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    const ok = ioPort.import(text);
    setImportMsg(ok ? 'imported' : 'invalid file');
    setTimeout(() => setImportMsg(null), 2000);
    e.target.value = '';
  };

  return (
    <div className="space-y-4 text-sm text-slate-200">
      {/* Quick-access map controls */}
      <section>
        <SectionTitle>Map</SectionTitle>
        <label className="block text-xs text-slate-400 mt-2">
          Radius: {settings.radiusKm.toLocaleString()} km
        </label>
        <input
          type="range" min="1000" max="20000" step="500"
          value={settings.radiusKm}
          onChange={(e) => onUpdate({ radiusKm: +e.target.value })}
          className="w-full"
        />
        <label className="block text-xs text-slate-400 mt-2">Maidenhead locator</label>
        <input
          type="text"
          value={locDraft}
          onChange={(e) => setLocDraft(e.target.value.toUpperCase())}
          onBlur={() => locValid && onUpdate({ locator: locDraft })}
          className={`w-full bg-slate-800 px-2 py-1 rounded font-mono ${
            locValid ? '' : 'ring-1 ring-red-500'}`}
        />
      </section>

      <section>
        <SectionTitle>Theme</SectionTitle>
        <div className="flex gap-1 mt-1">
          {THEMES.map((t) => (
            <button key={t.id}
                    onClick={() => onUpdate({ theme: t.id })}
                    className={`flex-1 py-1 rounded text-xs ${
                      settings.theme === t.id
                        ? 'bg-cyan-500 text-slate-900 font-semibold'
                        : 'bg-slate-800 hover:bg-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section>
        <SectionTitle>Visible spots</SectionTitle>
        <div className="flex gap-1 mt-1">
          {ROW_LIMITS.map((n) => (
            <button key={n}
                    onClick={() => onUpdate({ rowLimit: n })}
                    className={`flex-1 py-1 rounded text-xs ${
                      settings.rowLimit === n
                        ? 'bg-cyan-500 text-slate-900 font-semibold'
                        : 'bg-slate-800 hover:bg-slate-700'}`}>
              {n}
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between">
          <SectionTitle>Toolbar bands</SectionTitle>
          <div className="flex gap-1 text-[11px] text-slate-400">
            <button onClick={() => onUpdate({ visibleBands: [...ALL_BANDS] })}
                    className="hover:text-slate-200 px-1">all</button>
            <button onClick={() => onUpdate({ visibleBands: [...DEFAULT_VISIBLE_BANDS] })}
                    className="hover:text-slate-200 px-1">defaults</button>
            <button onClick={() => onUpdate({ visibleBands: [] })}
                    className="hover:text-slate-200 px-1">none</button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Pick which band chips appear in the top toolbar. MF/LF and UHF+
          are off by default — keeps the mobile chip row short.
        </p>
        <BandVisibilityPicker
          selected={settings.visibleBands || []}
          onChange={(v) => onUpdate({ visibleBands: v })}
        />
      </section>

      {/* Layered filter lists. Quick-filter chips live in the top toolbar. */}
      <section>
        <Filters settings={settings} onUpdate={onUpdate} />
      </section>

      <section>
        <SectionTitle>Profile</SectionTitle>
        <label className="block text-xs text-slate-400 mt-2">Callsign</label>
        <input
          type="text"
          value={settings.callsign}
          onChange={(e) => onUpdate({ callsign: e.target.value.toUpperCase() })}
          placeholder="optional"
          className="w-full bg-slate-800 px-2 py-1 rounded font-mono"
        />
      </section>

      <section className="pt-3 border-t border-slate-800 space-y-2">
        <SectionTitle>Settings file</SectionTitle>
        <div className="flex gap-1">
          <button onClick={onExport}
                  className="flex-1 py-1 bg-slate-800 hover:bg-slate-700 rounded text-xs">
            Export
          </button>
          <button onClick={() => fileInput.current?.click()}
                  className="flex-1 py-1 bg-slate-800 hover:bg-slate-700 rounded text-xs">
            Import
          </button>
        </div>
        <input ref={fileInput} type="file" accept="application/json"
               onChange={onImport} className="hidden" />
        {importMsg && <div className="text-xs text-slate-400 text-center">{importMsg}</div>}
        <button onClick={onReset}
                className="w-full py-1 bg-slate-800 hover:bg-slate-700 rounded text-xs">
          Reset to defaults
        </button>
      </section>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="text-xs uppercase tracking-wide text-slate-400">{children}</h2>;
}

function BandVisibilityPicker({ selected, onChange }) {
  const sel = new Set(selected);
  const toggle = (b) => {
    const next = new Set(sel);
    next.has(b) ? next.delete(b) : next.add(b);
    onChange(ALL_BANDS.filter((x) => next.has(x)));
  };
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {ALL_BANDS.map((b) => {
        const on = sel.has(b);
        return (
          <button
            key={b}
            type="button"
            onClick={() => toggle(b)}
            style={on ? { background: bandColor(b), borderColor: bandColor(b) } : undefined}
            className={`px-2 py-0.5 rounded border font-mono text-xs ${
              on
                ? 'text-slate-900 font-semibold'
                : 'bg-slate-800 border-slate-700 hover:bg-slate-700 text-slate-400'
            }`}>
            {b}
          </button>
        );
      })}
    </div>
  );
}
